import * as React from "react";

import {
  AddOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
} from "@mui/material";

import { AxiosError } from "axios";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { LoadingButton } from "@mui/lab";
import TextField from "@mui/material/TextField";
import { useCreateRemoteResource } from "../RemoteResoucesServices";
import {
  MCP_SERVER_TYPE,
  buildCreateMCPPayload,
  getEmptyMCPForm,
  isValidMCPForm,
  slugFromName,
} from "../mcpResource";
import MCPServerFields from "./MCPServerFields";
import { useMcpServersEnabled } from "src/config/posthog";

/**
 * A displayable message for an API error.
 *
 * FastAPI's `detail` is a string for the errors our routers raise, but a
 * validation failure (422) sends an array of `{loc, msg, ...}` objects, and
 * rendering that as a React child throws. Every shape is reduced to text
 * here so no field validator can take a modal down.
 */
export const getErrorMessage = (error: unknown): string => {
  const fallback = "Something went wrong";
  if (!(error instanceof AxiosError)) return fallback;
  const detail: unknown = error.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item: unknown) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          const { msg, loc } = item as { msg?: unknown; loc?: unknown };
          const field = Array.isArray(loc) ? String(loc[loc.length - 1]) : "";
          return field ? `${field}: ${String(msg)}` : String(msg);
        }
        return "";
      })
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return error.message || fallback;
};

interface CreateResourceModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CreateResourceModal({
  open,
  setOpen,
}: CreateResourceModalProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const {
    mutate: createRemoteResource,
    isLoading: isCreateRemoteResourceLoading,
    isError: isCreateRemoteResourceError,
    error: createRemoteResourceError,
    reset: resetCreateRemoteResource,
  } = useCreateRemoteResource();
  const [resourceForm, setResourceForm] = React.useState({
    name: "",
    description: "",
    url: "",
    resource_type: "remote_mre",
    credentials: "",
    active: true,
  });
  const [mcpForm, setMcpForm] = React.useState(getEmptyMCPForm());
  // Slug follows the name until the user edits it directly.
  const [slugTouched, setSlugTouched] = React.useState(false);

  // PostHog rollout gate: the MCP type is offered only where the
  // mcp-servers flag is enabled for the account (off by default).
  const mcpServersEnabled = useMcpServersEnabled();
  const isMcp = resourceForm.resource_type === MCP_SERVER_TYPE;
  const mcpInvalid = isMcp && !isValidMCPForm(mcpForm);

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSave = () => {
    // Close/reset only on success — otherwise the modal would close before a 4xx
    // (e.g. an unenforced-gateway rejection) could render in the Alert above.
    createRemoteResource(
      {
        ...resourceForm,
        ...buildCreateMCPPayload(resourceForm.resource_type, mcpForm),
      },
      {
        onSuccess: () => {
          setResourceForm({
            name: "",
            description: "",
            url: "",
            resource_type: "remote_mre",
            credentials: "",
            active: true,
          });
          setMcpForm(getEmptyMCPForm());
          setSlugTouched(false);
          handleClose();
        },
      },
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setResourceForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
    if (name === "name" && !slugTouched) {
      setMcpForm((prev) => ({ ...prev, slug: slugFromName(value) }));
    }
  };

  const handleMcpChange = (
    field: keyof typeof mcpForm,
    value: string | number,
  ) => {
    if (field === "slug") setSlugTouched(true);
    setMcpForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setResourceForm((prevForm) => ({
      ...prevForm,
      [name]: checked,
    }));
  };

  React.useEffect(() => {
    resetCreateRemoteResource();
  }, [open, resetCreateRemoteResource]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      data-testid="rr-dialog-create"
    >
      <DialogTitle>Create Remote Resource</DialogTitle>
      <DialogContent dividers>
        {isCreateRemoteResourceError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(createRemoteResourceError)}
          </Alert>
        )}

        <Stack direction="column" gap={2} sx={{ height: "100%" }}>
          <TextField
            label="Resource Name"
            name="name"
            type="text"
            value={resourceForm.name}
            onChange={handleChange}
            fullWidth
            required
            data-testid="rr-create-input-name"
          />
          <TextField
            label="Resource Description"
            name="description"
            type="text"
            value={resourceForm.description}
            onChange={handleChange}
            fullWidth
            data-testid="rr-create-input-description"
          />
          <TextField
            select
            label="Resource Type"
            name="resource_type"
            value={resourceForm.resource_type}
            onChange={handleChange}
            fullWidth
            data-testid="rr-create-input-resource-type"
          >
            <MenuItem value="remote_mre">NucLearn Hub (datasets)</MenuItem>
            <MenuItem value="litellm_gateway">
              LiteLLM Gateway (models)
            </MenuItem>
            {mcpServersEnabled && (
              <MenuItem value={MCP_SERVER_TYPE}>MCP Server (tools)</MenuItem>
            )}
          </TextField>
          <TextField
            label="Resource URL"
            name="url"
            type="url"
            value={resourceForm.url}
            onChange={handleChange}
            fullWidth
            required
            helperText={
              resourceForm.resource_type === "litellm_gateway"
                ? "Gateway base URL, e.g. http://llm-gateway.nuclearn.internal/v1"
                : isMcp
                  ? "MCP endpoint, e.g. http://peaksmcp:8097/mcp (or .../sse for SSE-only servers)"
                  : undefined
            }
            data-testid="rr-create-input-url"
          />
          {isMcp && (
            <MCPServerFields
              form={mcpForm}
              onChange={handleMcpChange}
              testIdPrefix="rr-create"
              slugRequiredError={slugTouched || resourceForm.name !== ""}
            />
          )}
          <TextField
            fullWidth
            label={isMcp ? "API Key (optional)" : "Encrypted Credentials"}
            name="credentials"
            type={showPassword ? "text" : "password"}
            value={resourceForm.credentials}
            onChange={handleChange}
            data-testid="rr-create-input-credentials"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    data-testid="rr-create-btn-toggle-password"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlined />
                    ) : (
                      <VisibilityOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                name="active"
                checked={resourceForm.active}
                onChange={handleSwitchChange}
                data-testid="rr-create-switch-active"
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton
          autoFocus
          color="primary"
          variant="contained"
          disableElevation
          onClick={handleSave}
          startIcon={<AddOutlined />}
          loading={isCreateRemoteResourceLoading}
          disabled={!resourceForm.name || !resourceForm.url || mcpInvalid}
          data-testid="rr-create-btn-submit"
        >
          Create
        </LoadingButton>
        <Button
          color="inherit"
          variant="outlined"
          disableElevation
          onClick={handleClose}
          data-testid="rr-create-btn-cancel"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
